import React from "react";
import Container from "react-bootstrap/Container";
import "./index.css";
import Papa from "papaparse";
import { useSelector, useDispatch } from "react-redux";
import { replaceImportedCart, setImportedQuoteState, setQuoteVersion } from "../../../app/features/counter/counterSlice";
import {
  calculateLineItem,
  getOptionEntries,
  DEFAULT_STANDARD_FIELDS,
  getProductSku,
  getProductDisplayName,
} from "../../Templates/quoteCalculator";
import { VERSION, LEGACY_VERSION_LABEL } from "../../../config/Version";

function FinalCart() {
  const dispatch = useDispatch();

  const cameras = useSelector((state) => state.counter1.selectedCamera || []);
  const cabling = useSelector((state) => state.counter1.selectedCabling || []);
  const labor = useSelector((state) => state.counter1.selectedLabor || []);
  const nvr = useSelector((state) => state.counter1.selectedNVR || []);
  const poe = useSelector((state) => state.counter1.selectedPoE || []);
  const hardware = useSelector((state) => state.counter1.selectedHardWare || []);
  const special = useSelector((state) => state.counter1.selectedSpecial || []);
  const quoteVersion = useSelector((state) => state.counter1.quoteVersion || VERSION);
  const customerUserData = useSelector((state) => state.counter1.customerData || {});

  const fileInputRef = React.useRef(null);

  const cartItems = [
    ...cameras,
    ...cabling,
    ...labor,
    ...nvr,
    ...poe,
    ...hardware,
    ...special,
  ];

  const getCategoryBucket = (item) => {
    if (!item || typeof item !== "object") return "selectedSpecial";

    if (
      item.cart_bucket &&
      [
        "selectedCamera",
        "selectedCabling",
        "selectedLabor",
        "selectedNVR",
        "selectedPoE",
        "selectedHardWare",
        "selectedSpecial",
      ].includes(item.cart_bucket)
    ) {
      return item.cart_bucket;
    }

    if (
      item.category === "DVRs & NVRs/NVR Recorders (IP Cameras)" ||
      item.category ===
        "DVRs & NVRs/NVR Recorders (IP Cameras)@Special/Software and Dongles" ||
      item.NVR_Name ||
      item.nvrName ||
      item.NVR_Quantity
    ) {
      return "selectedNVR";
    }

    if (item.category === "Camera" || item.Camera_Quantity) {
      return "selectedCamera";
    }

    if (item.category === "Cabling" || item.cableColor || item["Cable Color"]) {
      return "selectedCabling";
    }

    if (item.category === "Labor" || item.extra_field_4 !== undefined) {
      return "selectedLabor";
    }

    if (item.category === "Poe" || item.Port_Name || item.Port_Quantity) {
      return "selectedPoE";
    }

    if (
      item.category === "Hardware" ||
      item.Hardware_Total_data ||
      item.HardWare_Base_Price ||
      item.Hardware_New_Final_Price
    ) {
      return "selectedHardWare";
    }

    if (item.category === "Special" || item.Camera_Category_Name) {
      return "selectedSpecial";
    }

    return "selectedSpecial";
  };

  const getDisplayName = (item) => getProductDisplayName(item);

  const getImageSrc = (item) => {
    const img = item.thumbnail || item.image1 || "";
    if (!img) {
      return "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";
    }
    if (img.startsWith("http")) return img;
    return "https://platinumcctv.com/" + img;
  };

  const getVisibleOptionEntries = (item) => {
    const entries = [...getOptionEntries(item)];

    Object.keys(item || {}).forEach((key) => {
      if (!key.endsWith("_price")) return;

      const name = key.slice(0, -6);
      const value = item[name];

      if (DEFAULT_STANDARD_FIELDS.has(name)) return;
      if (/^option\d+$/.test(name)) return;
      if (value === undefined || value === null || value === "") return;

      const alreadyIncluded = entries.some(
        (entry) => entry.name === name && String(entry.value) === String(value)
      );

      if (!alreadyIncluded) {
        entries.push({
          name,
          value,
          price: Number(item[key] || 0),
        });
      }
    });

    return entries;
  };

  const cartTotal = cartItems.reduce((sum, item) => {
    return sum + calculateLineItem(item).lineTotal;
  }, 0);

  const normalizeImportedItem = (item, bucket, row = {}) => {
    const uniqueID =
      item.uniqueID ||
      row.uniqueID ||
      Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

    const numOptions = item.num_options || item.option || row.num_options || "";

    return {
      ...item,
      uniqueID,
      num_options: numOptions,
      cart_bucket: bucket,
    };
  };

  const buildExportRows = () => {
    const metadataRow = {
      row_type: "quote_metadata",
      app_version: VERSION,
      quote_version: VERSION,
      exported_with_version: VERSION,
    };

    const itemRows = cartItems.map((item) => {
      const calc = calculateLineItem(item);
      const options = getVisibleOptionEntries(item);
      const cartBucket = getCategoryBucket(item);

      const row = {
        cart_bucket: cartBucket,
        uniqueID: item.uniqueID || "",
        num_options: item.num_options || item.option || "",
        sku: getProductSku(item),
        name: getDisplayName(item),
        quantity: calc.quantity,
        unit_price: calc.unitPrice,
        option_unit_cost: calc.optionUnitTotal,
        option_cost: calc.optionTotal,
        line_total: calc.lineTotal,
        raw_item_json: JSON.stringify({ ...item, cart_bucket: cartBucket }),
        options_json: JSON.stringify(options),
      };

      options.forEach((option, index) => {
        const slot = index + 1;
        row[`option${slot}_name`] = option.name;
        row[`option${slot}_value`] = option.value;
        row[`option${slot}_price`] = option.price;
      });

      return row;
    });

    return [metadataRow, ...itemRows];
  };

  const sanitizeFileNamePart = (value, fallback = "") => {
    const text = String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ");

    return text || fallback;
  };

  const getQuoteNumber = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear().toString().slice(-2);
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");
    const lastName = sanitizeFileNamePart(customerUserData.customerLastName, "XXX");
    const firstThreeLetters = lastName ? lastName.substring(0, 3) : "XXX";

    return `${year}${month}${day}-${firstThreeLetters}`;
  };

  const getCustomerFileNamePart = () => {
    const businessName = sanitizeFileNamePart(customerUserData.businessName);
    if (businessName) return businessName;

    const lastName = sanitizeFileNamePart(customerUserData.customerLastName, "Customer");
    return `${lastName} Home`;
  };

  const getQuoteBaseFileName = () => getQuoteNumber();

  const downloadBlobFallback = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  };

  const exportCartCsv = async () => {
    const csv = Papa.unparse(buildExportRows());
    const fileName = `${getQuoteBaseFileName()}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: "CSV files",
              accept: { "text/csv": [".csv"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        dispatch(setQuoteVersion(VERSION));
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.warn("Save As dialog failed; falling back to standard download.", error);
      }
    }

    downloadBlobFallback(blob, fileName);
    dispatch(setQuoteVersion(VERSION));
  };

  const importCartCsv = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const imported = {
          selectedCamera: [],
          selectedCabling: [],
          selectedLabor: [],
          selectedNVR: [],
          selectedPoE: [],
          selectedHardWare: [],
          selectedSpecial: [],
        };

        let importedQuoteVersion = null;

        (results.data || []).forEach((row) => {
          if (row.row_type === "quote_metadata") {
            importedQuoteVersion = row.quote_version || row.app_version || row.exported_with_version || null;
            return;
          }

          let item = null;

          if (row.raw_item_json) {
            try {
              item = JSON.parse(row.raw_item_json);
            } catch (error) {
              item = null;
            }
          }

          if (!item) {
            const bucketFromRow = row.cart_bucket || "selectedSpecial";
            const quantity = Number(row.quantity || 1);
            const unitPrice = Number(row.unit_price || 0);
            const optionUnitCost = Number(row.option_unit_cost || 0);
            const lineTotal = Number(row.line_total || 0);

            item = {
              uniqueID:
                row.uniqueID ||
                Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
              num_options: row.num_options,
              id: row.sku,
              Product_Name: row.sku,
              name: row.name,
              Name: row.name,
              quantity,
              Product_Quantity: quantity,
              pricePerItem: unitPrice,
              Product_Base_Price: unitPrice,
              totalPriceForItem: unitPrice + optionUnitCost,
              Product_Final_Price: lineTotal,
            };

            if (bucketFromRow === "selectedCamera") {
              item.category = "Camera";
              item.Camera_Quantity = quantity;
              item.Camera_Base_Price = unitPrice;
              item.Camera_Final_Price = unitPrice + optionUnitCost;
            } else if (bucketFromRow === "selectedCabling") {
              item.category = "Cabling";
            } else if (bucketFromRow === "selectedLabor") {
              item.category = "Labor";
              item.extra_field_4 = item.extra_field_4 || "0";
            } else if (bucketFromRow === "selectedNVR") {
              item.category = "DVRs & NVRs/NVR Recorders (IP Cameras)";
            } else if (bucketFromRow === "selectedPoE") {
              item.category = "Poe";
              item.Port_Name = row.sku;
              item.Port_Quantity = quantity;
              item.Port_Base_Price = unitPrice;
              item.poeFinalPrice = unitPrice;
            } else if (bucketFromRow === "selectedHardWare") {
              item.category = "Hardware";
              item.basePrice3 = unitPrice;
            } else {
              item.category = "Special";
              item.Camera_Quantity = quantity;
              item.Camera_Base_Price = unitPrice;
              item.Camera_Final_Price = unitPrice + optionUnitCost;
            }

            for (let i = 1; i <= 12; i += 1) {
              const optionName = row[`option${i}_name`];
              if (optionName) {
                item[optionName] = row[`option${i}_value`];
                item[`${optionName}_price`] = Number(row[`option${i}_price`] || 0);
              }
            }
          }

          const bucket = row.cart_bucket || getCategoryBucket(item);
          if (!imported[bucket]) imported[bucket] = [];
          imported[bucket].push(normalizeImportedItem(item, bucket, row));
        });

        const safeImportedQuoteVersion = importedQuoteVersion || LEGACY_VERSION_LABEL;
        dispatch(setImportedQuoteState({ quoteVersion: safeImportedQuoteVersion }));
        dispatch(setQuoteVersion(safeImportedQuoteVersion));

        dispatch(replaceImportedCart(imported));
        event.target.value = "";
      },
    });
  };

  return (
    <Container fluid className="final_container py-5">
      <div className="card p-4">
        <div className="row p-2">
          <div className="col-md-7 cart">
            <div className="title">
              <div className="row">
                <div className="col">
                  <h4>
                    <b>Shopping Cart</b>
                  </h4>
                </div>
                <div className="col d-flex justify-content-end text-muted">
                  {cartItems.length} items
                </div>
              </div>
            </div>

            {cartItems.length === 0 ? (
              <div className="row border-top border-bottom">
                <div className="row main align-items-center py-4">
                  <div className="col text-muted">No items in cart.</div>
                </div>
              </div>
            ) : (
              cartItems.map((item, index) => {
                const calc = calculateLineItem(item);
                const options = getVisibleOptionEntries(item);

                return (
                  <div
                    className="row border-top border-bottom"
                    key={item.uniqueID || index}
                  >
                    <div className="row main align-items-center py-4">
                      <div className="col-2">
                        <img
                          className="img-fluid"
                          src={getImageSrc(item)}
                          alt={getDisplayName(item)}
                        />
                      </div>
                      <div className="col">
                        <div className="row text-muted fs-6 fw-bold">
                          {getDisplayName(item)}
                        </div>
                        <div className="row item_desc">
                          {options.length
                            ? options
                                .map(
                                  (option) =>
                                    `${option.name}: ${option.value} ($${Number(
                                      option.price || 0
                                    ).toFixed(2)})`
                                )
                                .join(", ")
                            : "No selected options"}
                        </div>
                      </div>
                      <div className="col">{calc.quantity}</div>
                      <div className="col">${calc.lineTotal.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })
            )}

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-secondary" onClick={exportCartCsv}>
                Export CSV
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Import CSV
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={importCartCsv}
              />
            </div>

            <div className="back-to-shop">
              <a href="/"> </a>
              <span className="text-muted">Back to shop</span>
            </div>
          </div>

          <div className="col-md-5 summary">
            <div>
              <h5>
                <b>Summary</b>
              </h5>
            </div>
            <hr />
            <div className="row">
              <div className="col" style={{ paddingLeft: "0" }}>
                ITEMS {cartItems.length}
              </div>
              <div className="col text-right">${cartTotal.toFixed(2)}</div>
            </div>
            <div
              className="row"
              style={{
                border: "1px solid rgba(0,0,0,0.1)",
                padding: "2vh 0",
              }}
            >
              <div className="col">TOTAL PRICE</div>
              <div className="col text-right">${cartTotal.toFixed(2)}</div>
            </div>
            <button className="btn">CHECKOUT</button>
          </div>
        </div>
      </div>
    </Container>
  );
}

export default FinalCart;
